import { cn } from "@tcg/ui";
import { Delete } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface NumPadProps {
  /** Digits typed so far (a string — no arithmetic). */
  value: string;
  onChange: (value: string) => void;
  /** Longest number (PIN 6, yen 9). */
  maxLength?: number;
  /** Show 00 / 000 keys (money entry). */
  money?: boolean;
  /** Hide the digits (PIN). Implies `digits`. */
  masked?: boolean;
  /**
   * A code, not an amount (PIN, pairing code): every key just appends, so "0123" stays "0123".
   * Amounts (the default) drop a leading zero ("0" then "5" → "5"). A masked pad is always one.
   */
  digits?: boolean;
  label: string;
  disabled?: boolean;
  onEnter?: () => void;
  enterLabel?: string;
  className?: string;
}

/**
 * Large number pad (T07-22): PIN, お預かり, amounts. Works with fingers only; a hardware
 * keyboard also works (digits, Backspace, Enter). The value is a digit string — turning it into
 * yen is `Number.parseInt` at the edge, never money arithmetic here.
 */
export function NumPad({
  value,
  onChange,
  maxLength = 9,
  money = false,
  masked = false,
  digits = false,
  label,
  disabled = false,
  onEnter,
  enterLabel,
  className,
}: NumPadProps) {
  const { t } = useTranslation();
  const press = (key: string) => {
    if (disabled) {
      return;
    }
    // an amount drops its leading zero; a PIN / code keeps every digit (0123 is a valid PIN)
    const code = digits || masked;
    const next = !code && value === "0" ? key.replace(/^0+/, "") || "0" : value + key;
    onChange(next.slice(0, maxLength));
  };
  const keys = ["7", "8", "9", "4", "5", "6", "1", "2", "3"];
  return (
    <div
      role="group"
      aria-label={label}
      className={cn("grid grid-cols-3 gap-2", className)}
      onKeyDown={(e) => {
        if (/^[0-9]$/.test(e.key)) {
          press(e.key);
          e.preventDefault();
        } else if (e.key === "Backspace") {
          onChange(value.slice(0, -1));
          e.preventDefault();
        } else if (e.key === "Enter" && onEnter) {
          onEnter();
          e.preventDefault();
        }
      }}
    >
      <output
        aria-live="polite"
        className="col-span-3 min-h-pos-touch rounded-md border bg-muted px-4 text-right text-3xl font-semibold tabular-nums leading-[56px]"
      >
        {masked ? "●".repeat(value.length) : value || " "}
      </output>
      {keys.map((k) => (
        <PadKey key={k} onPress={() => press(k)} disabled={disabled}>
          {k}
        </PadKey>
      ))}
      {money ? (
        <PadKey onPress={() => press("00")} disabled={disabled}>
          00
        </PadKey>
      ) : (
        <PadKey onPress={() => onChange("")} disabled={disabled} label={t("pin.clear")}>
          C
        </PadKey>
      )}
      <PadKey onPress={() => press("0")} disabled={disabled}>
        0
      </PadKey>
      <PadKey
        onPress={() => onChange(value.slice(0, -1))}
        disabled={disabled}
        label={t("pin.delete")}
      >
        <Delete aria-hidden className="size-7" />
      </PadKey>
      {onEnter ? (
        <button
          type="button"
          onClick={onEnter}
          disabled={disabled}
          className="col-span-3 min-h-pos-touch rounded-md bg-primary text-xl font-semibold text-primary-foreground disabled:opacity-50"
        >
          {enterLabel}
        </button>
      ) : null}
    </div>
  );
}

function PadKey({
  children,
  onPress,
  disabled,
  label,
}: {
  children: React.ReactNode;
  onPress: () => void;
  disabled: boolean;
  label?: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onPress}
      className="flex min-h-pos-touch items-center justify-center rounded-md border bg-background text-2xl font-semibold tabular-nums active:bg-accent disabled:opacity-50"
    >
      {children}
    </button>
  );
}
